import { useCallback, useMemo, useRef, useState } from "react";
import { AlertCircle } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { TrainingTeenEditor } from "@/components/settings/training-teen-editor";
import { TrainingTeenSidebar } from "@/components/settings/training-teen-sidebar";
import { useTrainingTeenContent } from "@/lib/api/training-simulator-content";

export function TrainingTeenSettings() {
  const { data, isLoading, isError } = useTrainingTeenContent();
  const teens = useMemo(
    () =>
      data?.modules.flatMap((module) =>
        module.levels.flatMap((level) =>
          level.teens.map((teen) => ({
            module,
            level,
            teen,
          })),
        ),
      ) ?? [],
    [data],
  );
  const [selectedTeenId, setSelectedTeenId] = useState<string | null>(null);
  const editorDirtyRef = useRef(false);

  // Fall back to the first teen so no selection effect is needed, and so a
  // teen removed by a refetch can't strand the selection.
  const selected =
    teens.find(({ teen }) => teen.id === selectedTeenId) ?? teens[0];

  const handleDirtyChange = useCallback((dirty: boolean) => {
    editorDirtyRef.current = dirty;
  }, []);

  const handleSelectTeen = (teenId: string) => {
    if (teenId === selected?.teen.id) return;
    if (
      editorDirtyRef.current &&
      !window.confirm(
        "You have unsaved changes that will be lost. Switch teens anyway?",
      )
    ) {
      return;
    }
    setSelectedTeenId(teenId);
  };

  if (isLoading) {
    return (
      <div className="grid gap-4 lg:grid-cols-[300px_minmax(0,1fr)]">
        <Skeleton className="h-[480px] rounded-lg" />
        <div className="space-y-4">
          <Skeleton className="h-20 rounded-lg" />
          <Skeleton className="h-64 rounded-lg" />
          <Skeleton className="h-64 rounded-lg" />
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center gap-2 rounded-md bg-red-50 p-4 text-sm text-red-700">
        <AlertCircle className="h-4 w-4 shrink-0" />
        Failed to load training simulator teens
      </div>
    );
  }

  if (!selected) {
    return (
      <div className="rounded-md border border-gray-100 p-4 text-sm text-gray-600">
        No simulator teen profiles are available.
      </div>
    );
  }

  return (
    <div className="grid items-start gap-4 lg:grid-cols-[300px_minmax(0,1fr)]">
      <TrainingTeenSidebar
        modules={data?.modules ?? []}
        teenCount={teens.length}
        selectedTeenId={selected.teen.id}
        onSelectTeen={handleSelectTeen}
      />
      <TrainingTeenEditor
        key={selected.teen.id}
        module={selected.module}
        level={selected.level}
        teen={selected.teen}
        onDirtyChange={handleDirtyChange}
      />
    </div>
  );
}
